<template>
  <!-- Edit details of a third party in a side panel, instead of the old pop-up and the inline address form (US 9371) -->
  <V2SidePanel ref="panel"
               :kind="$t('vendor_hero.kind')"
               :title="$t('system_hero.panel.edit_title')"
               :intro="$t('system_hero.panel.edit_intro')"
               :busy="saving"
               qid="vendor-edit-panel"
               @hidden="error = ''">
    <b-alert v-if="error" show variant="danger" qid="edit-tp-error">{{ error }}</b-alert>

    <!-- Logo: saved straight away, apart from the other fields -->
    <V2LogoPicker :endpoint="'vendors/' + vendor.slug + '/logo'"
                  :value="vendor.logo_large || vendor.logo"
                  :name="vendor.name"
                  qid="vendor-edit-logo"
                  @saved="$emit('logo', $event)"></V2LogoPicker>

    <div class="v2-field">
      <label class="v2-field__label" for="vx-edit-name">{{ $t('maintenance.third_parties.create.name_label') }} <span class="v2-field__req">*</span></label>
      <b-form-input id="vx-edit-name" v-model="form.name" :maxLength="255" required qid="edit-tp-form-name"></b-form-input>
    </div>
    <div class="v2-field">
      <label class="v2-field__label" for="vx-edit-description">{{ $t('maintenance.third_parties.create.description_label') }}</label>
      <b-form-textarea id="vx-edit-description" v-model="form.description" rows="3" max-rows="8" qid="vendor-edit-description"></b-form-textarea>
    </div>
    <div class="v2-field">
      <label class="v2-field__label" for="vx-edit-address">{{ $t('administration.clients.create.street_label') }}</label>
      <b-form-input id="vx-edit-address" v-model="form.address" :maxLength="255" qid="vendor-edit-address"></b-form-input>
    </div>
    <div class="v2-field__two">
      <div class="v2-field">
        <label class="v2-field__label" for="vx-edit-postcode">{{ $t('administration.clients.create.postcode_label') }}</label>
        <b-form-input id="vx-edit-postcode" v-model="form.postal_code" :maxLength="255" qid="vendor-edit-postcode"></b-form-input>
      </div>
      <div class="v2-field">
        <label class="v2-field__label" for="vx-edit-city">{{ $t('administration.clients.create.city_label') }}</label>
        <b-form-input id="vx-edit-city" v-model="form.city" :maxLength="255" qid="vendor-edit-city"></b-form-input>
      </div>
    </div>
    <div class="v2-field__two">
      <div class="v2-field">
        <label class="v2-field__label" for="vx-edit-state">{{ $t('administration.clients.create.state_label') }}</label>
        <b-form-input id="vx-edit-state" v-model="form.state" :maxLength="255" qid="vendor-edit-state"></b-form-input>
      </div>
      <div class="v2-field">
        <label class="v2-field__label" for="vx-edit-country">{{ $t('administration.clients.create.country_label') }}</label>
        <b-form-select id="vx-edit-country" v-model="form.country" :options="countryOptions" qid="vendor-edit-country"></b-form-select>
      </div>
    </div>
    <div class="v2-field">
      <label class="v2-field__label" for="vx-edit-number">{{ $t('administration.clients.create.cvr_label') }}</label>
      <b-form-input id="vx-edit-number" v-model="form.company_number" :maxLength="255" qid="vendor-edit-company-number"></b-form-input>
    </div>

    <template #foot>
      <span v-if="vendor.updated_by" class="v2-panel__note">
        <font-awesome-icon icon="clock"/>{{ $t('system_hero.panel.last_change', { name: vendor.updated_by.name, when: fromNow(vendor.updated_at) }) }}
      </span>
      <span class="v2-panel__grow"></span>
      <b-button variant="secondary" qid="edit-tp-cancel-button" @click="$refs.panel.hide()">{{ $t('maintenance.third_parties.create.cancel') }}</b-button>
      <b-button variant="success" :disabled="saving || !form.name.trim()" qid="edit-tp-submit-button" @click="save">
        <font-awesome-icon :icon="saving ? 'spinner' : 'check'" :class="{ 'fa-spin': saving }"/> {{ $t('system_hero.panel.save') }}
      </b-button>
    </template>
  </V2SidePanel>
</template>

<script>
  import moment from 'moment';
  import piincHttp from '@/util/configureAxios.js';
  import V2SidePanel from '@/components/V2SidePanel.vue';
  import V2LogoPicker from '@/components/V2LogoPicker.vue';

  const FIELDS = ['name', 'description', 'address', 'postal_code', 'city', 'state', 'country', 'company_number'];

  export default {
    name: 'VendorEditPanel',
    components: {
      V2SidePanel,
      V2LogoPicker
    },
    props: {
      vendor: { type: Object, required: true }
    },
    data() {
      return {
        form: { name: '' },
        countries: [],
        saving: false,
        error: ''
      }
    },
    computed: {
      countryOptions() {
        let names = this.countries.map(country => country.name);
        // A country that isn't in the list (typed in before) stays selectable
        if (this.form.country && !names.includes(this.form.country)) {
          names.unshift(this.form.country);
        }
        return [{ value: null, text: '–' }].concat(names.map(name => ({ value: name, text: name })));
      }
    },
    methods: {
      show() {
        let form = {};
        FIELDS.forEach(key => {
          form[key] = this.vendor[key] || (key === 'country' ? null : '');
        });
        this.form = form;
        this.error = '';
        if (!this.countries.length) {
          this.loadCountries();
        }
        this.$refs.panel.show();
      },
      loadCountries() {
        let self = this;
        piincHttp.get('countries-with-flag').then(function(response) {
          self.countries = response.data || [];
        }, function() {});
      },
      fromNow(value) {
        return moment(value).fromNow();
      },
      save() {
        let self = this;
        let data = {};
        FIELDS.forEach(key => {
          let value = this.form[key];
          data[key] = typeof value === 'string' ? (value.trim() || null) : value;
        });
        this.saving = true;
        this.error = '';
        piincHttp.put('vendors/' + this.vendor.slug, data).then(function(response) {
          self.saving = false;
          self.$refs.panel.hide();
          self.$toastr('success', self.$t('maintenance.third_parties.vendors.update.success'));
          self.$emit('saved', response.data);
        }, function(error) {
          self.saving = false;
          self.error = (error.data && error.data.message) || self.$t('system_hero.panel.error');
        });
      }
    }
  }
</script>
